/**
 * @author      Michael Hettmer <mail@michael-hettmer.de>
 * @copyright   2019 Plusbyte UG (haftungsbeschränkt)
 * @license     {@link https://plusbyte.de Plusbyte License}
 */

import { EventChannel } from 'redux-saga';
import { all, put } from 'redux-saga/effects';

import { getFirebaseAnalytics } from '~/apis/firebase';
import { gameLogger } from '~/app';
import { setConnected, setCurrentState } from '~/game';
import {
  setEventLog,
  setLastCard,
  setLastCardHorseIndex,
  setLastTurnDicePlayerId,
  setLastTurnDices,
  setLastTurnDicesCur,
} from '~/overlay';
import { GameState, GameType } from '~/server/src/dtos';
import { Controller, View } from '~/server/src/interfaces';
import Turfmaster from '~/server/src/tmmodel';
import { actions as setupActions, RestoreGameParameters } from '~/setup';
import { setLastActiveGameId } from '~/setup/reconnectStorage';

import { SetupChannelEvent } from '../channelEvent';
import { takeUntilPreserving } from '../utils/saga';

function* sagaRestoreGame(
  view: View,
  controller: Controller,
  restoreGameParams: RestoreGameParameters,
  channel: EventChannel<SetupChannelEvent>,
) {
  if (!view || !controller || !channel) return;

  gameLogger.info({ mode: restoreGameParams.mode }, 'sagaRestoreGame: Starting restore process');

  const analytics = getFirebaseAnalytics();

  yield put(setupActions.setRestoreState('loading'));

  controller.receiveMsg(Turfmaster.stateMachine.RESTOREGAME, {
    view,
    gameId: restoreGameParams.preferredGameId,
    userId: restoreGameParams.userId,
  });
  const restoreResult = yield* takeUntilPreserving(channel, (event) => {
    return event.state === Turfmaster.stateMachine.RESTOREGAME;
  });
  const { state, params } = restoreResult.matched;
  const replayEvents: SetupChannelEvent[] = [...restoreResult.buffered];

  if (state === Turfmaster.stateMachine.RESTOREGAME) {
    gameLogger.debug({ state, params }, 'controller => client: RESTOREGAME');
    const restorableGameState: GameState | undefined = params?.game;

    if (restorableGameState) {
      // a game that could be restored does exist
      controller.receiveMsg(Turfmaster.stateMachine.REJOINGAME, {
        view,
        gameId: restoreGameParams.preferredGameId ?? restorableGameState.gameId,
        userId: restoreGameParams.userId,
      });
      const rejoinResult = yield* takeUntilPreserving(channel, (event) => {
        return event.state === Turfmaster.stateMachine.REJOINGAME;
      });
      const { state, params } = rejoinResult.matched;
      replayEvents.push(...rejoinResult.buffered);

      const game = controller.getGame();
      const board = controller.getBoard();

      if (state === Turfmaster.stateMachine.REJOINGAME && game && board) {
        gameLogger.debug({ state, params, game, board }, 'controller => client: REJOINGAME');

        const lastTurnDices = game.turndices.length > 0 ? game.turndices : [-1, -1];
        const previousTurnHorseIndex =
          game.turntype === Turfmaster.turntypes.CARDS && game.turnorder.length > 0
            ? game.turnorder[(game.turnpos - 1 + game.turnorder.length) % game.turnorder.length]
            : -1;

        // rejoin successful
        yield all([
          put(setupActions.setPlayerId(params.playerid)),
          put(setupActions.setGameId(restorableGameState.gameId)),
          put(
            restoreGameParams.mode === 'training'
              ? setupActions.setGameType(GameType.Training)
              : setupActions.setGameType(restorableGameState.gameType),
          ),
          put(setCurrentState(game.currentState)),
          put(setEventLog(restorableGameState.eventLog)),
          put(setConnected(true)),
          put(setupActions.setRestoreState('success')),

          // try to reconstruct lastTurnDice player and array
          put(setLastTurnDicePlayerId(game.turndiceplayerid)),
          put(setLastTurnDices(lastTurnDices)),
          put(setLastTurnDicesCur(game.turndicescur)),

          // Reconstruct last card horse index safely; card value itself is unknown after restore.
          put(setLastCardHorseIndex(previousTurnHorseIndex ?? -1)),
          put(setLastCard(-1)),
        ]);
        setLastActiveGameId(restorableGameState.gameId, restoreGameParams.userId);

        // If state is SELECTMOVE while restoring, re-trigger the current player so moves/bot advance.
        // Only send SELECTCARDDICE as human when it's the human's turn; rejoinGame() already triggered the bot.
        // Without this guard, sending SELECTCARDDICE when it's the bot's turn causes controller rejection and stuck games.
        if (game.currentState === Turfmaster.stateMachine.SELECTMOVE) {
          const horseid = game.turnorder[game.turnpos];
          const currentPlayerId = game.horses[horseid]?.playerid;
          if (currentPlayerId === params.playerid) {
            controller.receiveMsg(Turfmaster.stateMachine.SELECTCARDDICE, {
              playerid: params.playerid,
              horseid,
              carddiceid: -1,
            });
          }
        }

        analytics?.logEvent('restore_game', {
          connectionType: controller.getType(),
          playerCount: game.players.length,
          horseCountPerPlayer: game.horses.length / game.players.length,
        });

        return { ok: true, replayEvents };
      } else {
        // tbd handle unsuccessful rejoin
        gameLogger.warn({ state, params }, 'controller => client: UNKNOWN RESPONSE TO REJOINGAME');
      }
    } else {
      // tbd no game available
      gameLogger.info('sagaRestoreGame: No restorable game found for user');
    }
  } else {
    // tbd wrong response
    gameLogger.warn({ state, params }, 'sagaRestoreGame: Unexpected response to RESTOREGAME');
  }

  gameLogger.warn('sagaRestoreGame: Restore process failed');
  yield put(setupActions.setRestoreState('error'));

  return { ok: false, replayEvents };
}

export default sagaRestoreGame;
